import { useMemo } from "react";
import { Progress } from "@/components/ui/progress";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

interface Requirement {
  label: string;
  test: (password: string) => boolean;
}

const requirements: Requirement[] = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "Uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "Lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "Number", test: (p) => /[0-9]/.test(p) },
  { label: "Special character (!@#$%^&*)", test: (p) => /[!@#$%^&*]/.test(p) },
];

export function PasswordStrengthIndicator({ password, className }: PasswordStrengthIndicatorProps) {
  const { strength, label, color, passed } = useMemo(() => {
    if (!password) {
      return { strength: 0, label: "", color: "bg-muted", passed: 0 };
    }

    const passedRequirements = requirements.filter((req) => req.test(password)).length;
    const strengthPercent = (passedRequirements / requirements.length) * 100;

    let strengthLabel: string;
    let strengthColor: string;

    if (passedRequirements <= 1) {
      strengthLabel = "Very Weak";
      strengthColor = "bg-destructive";
    } else if (passedRequirements === 2) {
      strengthLabel = "Weak";
      strengthColor = "bg-orange-500";
    } else if (passedRequirements === 3) {
      strengthLabel = "Fair";
      strengthColor = "bg-yellow-500";
    } else if (passedRequirements === 4) {
      strengthLabel = "Good";
      strengthColor = "bg-blue-500";
    } else {
      strengthLabel = "Strong";
      strengthColor = "bg-green-500";
    }

    return {
      strength: strengthPercent,
      label: strengthLabel,
      color: strengthColor,
      passed: passedRequirements,
    };
  }, [password]);

  if (!password) return null;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Password strength</span>
          <span className={cn(
            "font-medium",
            passed <= 1 && "text-destructive",
            passed === 2 && "text-orange-500",
            passed === 3 && "text-yellow-500",
            passed === 4 && "text-blue-500",
            passed === 5 && "text-green-500"
          )}>
            {label}
          </span>
        </div>
        <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
          <div 
            className={cn("h-full transition-all duration-300", color)}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1">
        {requirements.map((req, index) => {
          const isPassed = req.test(password);
          return (
            <div 
              key={index} 
              className={cn(
                "flex items-center gap-2 text-xs transition-colors",
                isPassed ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
              )}
            >
              {isPassed ? (
                <Check className="h-3 w-3" />
              ) : (
                <X className="h-3 w-3" />
              )}
              <span>{req.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
